import type {
  IGoogleDriveAdapter,
  IGoogleAuthAdapter,
  GoogleDriveFile,
  GoogleDriveFileAppProperties,
} from '@app/domain/ports';
import { DriveApiError } from './errors';

/**
 * Google Drive API adapter implementation
 * Handles file operations in Google Drive (list, upload, download, delete)
 * Pure TypeScript implementation - framework-agnostic
 */
export class GoogleDriveAdapter implements IGoogleDriveAdapter {
  constructor(private readonly auth: IGoogleAuthAdapter) {}

  isAuthorized(): boolean {
    return this.auth.isAuthorized();
  }

  async requestConsent(customState?: Record<string, unknown>): Promise<void> {
    await this.auth.requestConsent(customState);
  }

  async authorize(opts?: { customState?: Record<string, unknown>; silent?: boolean }): Promise<boolean> {
    return this.auth.authorize({
      prompt: 'select_account',
      ...(opts?.customState && { customState: opts.customState }),
      ...(opts?.silent !== undefined && { silent: opts.silent }),
    });
  }

  async list(folder: string): Promise<GoogleDriveFile[]> {
    const accessToken = this.getAccessToken();

    const folderId = await this.getOrCreateAppFolder(folder);

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+trashed=false&spaces=drive&fields=files(id,name,createdTime,size,appProperties)&orderBy=createdTime desc`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
        cache: 'no-store',
      },
    );

    if (!response.ok)
      throw new DriveApiError(
        'https://www.googleapis.com/drive/v3/files',
        response.status,
        `Failed to list files: ${response.status}`,
      );

    const result = await response.json();
    return (result.files || []).map(
      (file: {
        id: string;
        name: string;
        createdTime: string;
        size: string;
        appProperties?: GoogleDriveFileAppProperties;
      }) => ({
        ...file,
        size: parseInt(file.size, 10) || 0,
      }),
    );
  }

  async upload(folderName: string, fileName: string, data: Uint8Array): Promise<string> {
    const accessToken = this.getAccessToken();

    const folderId = await this.getOrCreateAppFolder(folderName);

    // Use multipart upload for file content
    const metadata = {
      name: fileName,
      mimeType: 'application/octet-stream',
      parents: [folderId],
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', new Blob([data.buffer as ArrayBuffer], { type: 'application/octet-stream' }));

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: form,
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new DriveApiError(
        'https://www.googleapis.com/upload/drive/v3/files',
        response.status,
        `Upload failed: ${response.status} ${errorText}`,
      );
    }

    const result = await response.json();
    console.log('✓ Backup uploaded to Google Drive:', result.name);
    return result.id;
  }

  async download(fileId: string): Promise<Uint8Array> {
    const accessToken = this.getAccessToken();

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok)
      throw new DriveApiError(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        response.status,
        `Failed to download file: ${response.status}`,
      );

    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  async delete(fileId: string): Promise<void> {
    const accessToken = this.getAccessToken();

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok)
      throw new DriveApiError(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        response.status,
        `Failed to delete file: ${response.status}`,
      );

    console.log('✓ File deleted from Google Drive:', fileId);
  }

  async updateFileProperties(fileId: string, appProperties: GoogleDriveFileAppProperties): Promise<void> {
    const accessToken = this.getAccessToken();

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=id`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appProperties }),
    });

    if (!response.ok)
      throw new DriveApiError(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        response.status,
        `Failed to update file properties: ${response.status}`,
      );

    console.log('✓ Updated file properties on Google Drive:', fileId);
  }

  /**
   * Get the access token for API calls
   */
  private getAccessToken(): string {
    const token = this.auth.getAccessToken();
    if (!token) throw new DriveApiError('', -1, `Access token not available. User may not be authenticated.`);

    return token;
  }

  /**
   * Get or create the folder hierarchy
   * Creates each folder in the path if it doesn't exist
   */
  private async getOrCreateAppFolder(folder: string): Promise<string> {
    const folderNames = folder.split('/');
    let parentId = 'root';

    for (const folderName of folderNames) {
      parentId = await this.getOrCreateFolder(folderName, parentId);
    }

    return parentId;
  }

  /**
   * Get or create a single folder by name within a parent folder
   */
  private async getOrCreateFolder(name: string, parentId: string): Promise<string> {
    const accessToken = this.getAccessToken();

    // Search for existing folder in parent
    const parentQuery = parentId === 'root' ? "'root' in parents" : `'${parentId}' in parents`;
    const query = `name='${name}' and ${parentQuery} and mimeType='application/vnd.google-apps.folder' and trashed=false`;

    const searchUrl = new URL('https://www.googleapis.com/drive/v3/files');
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('spaces', 'drive');
    searchUrl.searchParams.set('fields', 'files(id,name)');

    const searchResponse = await fetch(searchUrl.toString(), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!searchResponse.ok) {
      throw new DriveApiError(
        searchUrl.toString(),
        searchResponse.status,
        `Failed to search for folder '${name}': ${searchResponse.status}`,
      );
    }

    const searchResult = await searchResponse.json();

    if (searchResult.files && searchResult.files.length > 0) {
      return searchResult.files[0].id;
    }

    // Create folder if not exists
    const createResponse = await fetch('https://www.googleapis.com/drive/v3/files?fields=id', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [parentId],
      }),
    });

    if (!createResponse.ok) {
      throw new DriveApiError(
        'https://www.googleapis.com/drive/v3/files',
        createResponse.status,
        `Failed to create folder '${name}': ${createResponse.status}`,
      );
    }

    const createResult = await createResponse.json();
    console.log(`✓ Created folder in Google Drive: ${name}`);
    return createResult.id;
  }
}
